import {
  ALLOWED_COST_DOCUMENT_TYPES,
  ApiError,
  type InventoryCost,
  MAX_COST_DOCUMENT_BYTES,
  queryKeys,
} from '@mevaser/shared'
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  Input,
  useToast,
} from '@mevaser/ui'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Paperclip } from 'lucide-react'
import { useRef, useState } from 'react'
import { api } from '../lib/api.js'

const ALLOWED_COST_DOCUMENT_TYPE_LIST = ALLOWED_COST_DOCUMENT_TYPES.join(',')

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export interface SaleCostLine {
  id: string
  productName: string
  qty: number
}

/**
 * t1317: adds a cost of goods, with the vendor and their invoice or receipt,
 * to a line that has already been sold. The API turns it into an add-on cost
 * line on the sold device (IMEI) or product, so it shows up in Purchasing and
 * in the cost of goods report.
 */
export function AddSaleCostDialog({
  line,
  orderId,
  onClose,
}: {
  line: SaleCostLine | null
  orderId: string
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [label, setLabel] = useState('Cost of goods')
  const [amount, setAmount] = useState('')
  const [vendor, setVendor] = useState('')
  const [invoiceRef, setInvoiceRef] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  function reset() {
    setLabel('Cost of goods')
    setAmount('')
    setVendor('')
    setInvoiceRef('')
    setFile(null)
  }

  const create = useMutation({
    mutationFn: () =>
      api.post<InventoryCost>('/costs', {
        orderItemId: line?.id,
        kind: 'addon',
        label: label.trim(),
        amountCents: Math.round(Number(amount || 0) * 100),
        vendor: vendor.trim() || null,
        invoiceRef: invoiceRef.trim() || null,
      }),
    onSuccess: async (created) => {
      if (file) {
        try {
          const formData = new FormData()
          formData.append('file', file)
          await api.postFile(`/costs/${created.id}/document`, formData)
        } catch (error) {
          toast.error(
            'Cost recorded, but the invoice file could not be attached',
            error instanceof ApiError
              ? error.message
              : 'Open it in Purchasing and attach it there.',
          )
        }
      }
      await queryClient.invalidateQueries({ queryKey: queryKeys.purchasing.all() })
      await queryClient.invalidateQueries({ queryKey: queryKeys.purchasing.costs({ orderId }) })
      toast.success('Cost added')
      reset()
      onClose()
    },
    onError: (error: Error) =>
      toast.error('Could not add the cost', error instanceof ApiError ? error.message : undefined),
  })

  function handleFileChange(picked: File | null) {
    if (!picked) {
      setFile(null)
      return
    }
    if (!(ALLOWED_COST_DOCUMENT_TYPES as readonly string[]).includes(picked.type)) {
      toast.error('Could not attach', 'Invoices must be a PDF, JPEG, PNG or WebP image.')
      return
    }
    if (picked.size > MAX_COST_DOCUMENT_BYTES) {
      toast.error('Could not attach', `${picked.name} is larger than 10 MB.`)
      return
    }
    setFile(picked)
  }

  const amountNumber = Number(amount)
  const valid =
    label.trim() !== '' &&
    amount.trim() !== '' &&
    Number.isFinite(amountNumber) &&
    amountNumber >= 0

  return (
    <Dialog
      open={line !== null}
      onOpenChange={(next) => {
        if (!next) {
          reset()
          onClose()
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add a cost{line ? ` — ${line.productName}` : ''}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field label="Label" required>
            {(props) => (
              <Input {...props} value={label} onChange={(e) => setLabel(e.target.value)} />
            )}
          </Field>
          <Field
            label="Amount"
            required
            hint={
              line && line.qty > 1
                ? `In dollars. The total for all ${line.qty} on this line, as on the invoice.`
                : 'In dollars.'
            }
          >
            {(props) => (
              <Input
                {...props}
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
              />
            )}
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Vendor">
              {(props) => (
                <Input {...props} value={vendor} onChange={(e) => setVendor(e.target.value)} />
              )}
            </Field>
            <Field label="Invoice reference">
              {(props) => (
                <Input
                  {...props}
                  value={invoiceRef}
                  onChange={(e) => setInvoiceRef(e.target.value)}
                />
              )}
            </Field>
          </div>
          <Field label="Invoice or receipt" hint="Optional. PDF, JPEG, PNG or WebP, up to 10 MB.">
            {() => (
              <div className="flex items-center gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ALLOWED_COST_DOCUMENT_TYPE_LIST}
                  className="hidden"
                  onChange={(e) => {
                    handleFileChange(e.target.files?.[0] ?? null)
                    e.target.value = ''
                  }}
                />
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Paperclip className="size-4" /> {file ? 'Replace file' : 'Choose file'}
                </Button>
                {file ? (
                  <span className="text-xs text-foreground-muted">
                    {file.name} ({formatBytes(file.size)})
                  </span>
                ) : null}
              </div>
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => {
              reset()
              onClose()
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={create.isPending}
            disabled={!valid}
            onClick={() => create.mutate()}
          >
            Add cost
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
